/**
 * Enter Tasks sidebar view.
 *
 * Renders the current user's open Enter RCM worklist: a low-density list of
 * severity-dot + one-line-title rows that expand on tap to reveal detail + a
 * Close action (→ closes the underlying Enter message). Flat design — no
 * shadows/gradients, Tailwind tokens, tight grid. Severity is shown as a
 * colored dot only (no MED/HIGH text), per the product design.
 */

import { useState } from 'react';
import {
  AlertCircle,
  Building2,
  Check,
  ChevronRight,
  FileText,
  Layers,
  PauseCircle,
  ShieldCheck,
  UserRound,
  XCircle,
  Eye,
  Info,
  ListChecks,
  Loader2,
  Plug,
  RefreshCw,
  User,
  UserX,
  ExternalLink,
} from 'lucide-react';
import type { EnterTask } from '@ctrl-enter/types';
import type { TaskTypeId } from '@ctrl-enter/types';
import { TASK_TYPE_DEFINITIONS, parseTaskReason, taskMatchesType } from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { useEnterTasks } from '../hooks/use-enter-tasks';
import {
  useSetTasksFilter,
  useSetTasksTypeFilter,
  useTasksFilter,
  useTasksTypeFilter,
} from '../stores/enter-tasks-store';
import { EnterTasksOnboarding, useTasksOnboarding } from './enter-tasks-onboarding';
import { TaskDetailPanel } from './task-detail-panel';

const SEVERITY_DOT: Record<EnterTask['severity'], string> = {
  urgent: 'bg-destructive',
  high: 'bg-amber-500',
  normal: 'bg-muted-foreground',
  low: 'bg-muted-foreground/40',
};

const SEVERITY_LABEL: Record<EnterTask['severity'], string> = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

const SEVERITY_ORDER: EnterTask['severity'][] = ['urgent', 'high', 'normal', 'low'];

/**
 * Type chips, in Platform's order and with their icon vocabulary (pause for a
 * halt, x for a denial, shield for an authorization). 'done' is omitted: it
 * lists CLOSED messages (`filterOpen=0`) and our list only ever fetches open
 * work, so the chip would be a guaranteed dead end.
 */
const TYPE_CHIPS: { id: TaskTypeId; label: string; icon: typeof ListChecks }[] = [
  { id: 'all', label: 'All types', icon: Layers },
  { id: 'halt', label: 'Halts', icon: PauseCircle },
  { id: 'denial', label: 'Denials', icon: XCircle },
  { id: 'rejection', label: 'Rejections', icon: AlertCircle },
  { id: 'records', label: 'Records', icon: FileText },
  { id: 'patient', label: 'Patient info', icon: UserRound },
  { id: 'auth', label: 'Authorizations', icon: ShieldCheck },
];

function SeverityDot({ severity }: { severity: EnterTask['severity'] }) {
  return (
    <span
      className={cn('inline-block w-2 h-2 rounded-full shrink-0', SEVERITY_DOT[severity])}
      role="img"
      aria-label={`${SEVERITY_LABEL[severity]} priority`}
      title={`${SEVERITY_LABEL[severity]} priority`}
    />
  );
}

function formatDue(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── One task row (tap to expand) ───────────────────────────────────────────

/**
 * One row in the worklist. Tapping it opens the detail panel, the way
 * Platform's message list opens its Task panel — the row no longer expands
 * inline, because everything that expansion showed now lives in the panel
 * (and a halted-claim task can't be actioned from a row at all).
 */
function TaskRow({
  task,
  onOpen,
  highlighted = false,
}: {
  task: EnterTask;
  onOpen: () => void;
  /** True when this task belongs to the patient currently open in the EMR. */
  highlighted?: boolean;
}) {
  const due = formatDue(task.dueDate);
  // Structured coding-engine bodies are a fenced machine dump — the parsed
  // headline is what a person can actually read at row width.
  const parsed = parseTaskReason(task.body);
  const preview = parsed?.headline ?? task.body;

  return (
    <div className={cn('text-sm', highlighted && 'bg-primary/5 border-l-2 border-l-primary')}>
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-muted/40 transition-colors"
      >
        <SeverityDot severity={task.severity} />
        {/* Row subject + preview carry the same author text as the panel. */}
        <span data-phi className="flex-1 min-w-0">
          <span className="block truncate text-[13px] text-foreground">{task.title}</span>
          {preview && (
            <span className="block truncate text-[11px] text-muted-foreground/80">{preview}</span>
          )}
        </span>
        {task.assigneeName && (
          <span className="text-[10px] text-muted-foreground whitespace-nowrap inline-flex items-center gap-0.5 max-w-[90px] truncate">
            <User size={9} className="shrink-0" /> {task.assigneeName}
          </span>
        )}
        {due && (
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">{due}</span>
        )}
        {/* Platform puts a status pill on every row — it is the column a biller
            scans down. Same attention ramp as the detail header, so the two
            surfaces agree about what "needs you" looks like. */}
        <span
          className={cn(
            'inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap',
            task.open ? 'bg-attention/15 text-attention-text' : 'bg-muted text-muted-foreground'
          )}
        >
          <span
            className={cn(
              'w-1 h-1 rounded-full',
              task.open ? 'bg-attention' : 'bg-muted-foreground'
            )}
          />
          {task.open ? 'NEEDS YOU' : 'DONE'}
        </span>
        <ChevronRight size={13} className="text-muted-foreground shrink-0" />
      </button>
    </div>
  );
}


// ─── States ─────────────────────────────────────────────────────────────────

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center text-muted-foreground">
      {children}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Centered>
      <AlertCircle size={20} className="text-destructive" />
      <p className="text-[12px]">Couldn't load your tasks.</p>
      <button
        type="button"
        onClick={onRetry}
        className={cn(
          'inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md mt-1',
          'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors'
        )}
      >
        <RefreshCw size={11} /> Retry
      </button>
    </Centered>
  );
}

function NoIntegration({ onConnectIntegration }: { onConnectIntegration?: () => void }) {
  return (
    <Centered>
      <Plug size={20} className="text-primary-text" />
      <p className="text-[12px]">
        Connect your clinic's Enter integration to see your RCM tasks here.
      </p>
      {onConnectIntegration ? (
        <button
          type="button"
          onClick={onConnectIntegration}
          className={cn(
            'inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md mt-1',
            'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors'
          )}
        >
          <Plug size={11} /> Connect Enter
        </button>
      ) : (
        <p className="text-[11px] inline-flex items-center gap-1">
          Ask an admin to connect Enter in Integrations settings
          <ExternalLink size={10} />
        </p>
      )}
    </Centered>
  );
}

/**
 * Page shell for the Tasks view.
 *
 * This component was built for a ~380px extension sidebar, where filling the
 * width is correct. In a browser window it is the same component at 1500px, and
 * a task row stretched that wide puts the title and its status pill a screen
 * apart — unreadable, and nothing like Platform, whose list sits in a centred
 * card on the page background.
 *
 * `max-w-*` + `mx-auto` does nothing until the viewport exceeds the cap, so this
 * is a no-op in the sidebar and a centred column everywhere else — one code path
 * for both, which is what keeps the web and desktop copies identical.
 *
 * ONE width for the list AND the detail panel, deliberately: different caps
 * would shift the content sideways the moment you opened a task.
 */
const PAGE_SHELL = 'relative flex h-full bg-background text-card-foreground overflow-hidden';
const PAGE_COLUMN = 'flex flex-col h-full w-full max-w-3xl mx-auto bg-card overflow-hidden';

// ─── Main view ───────────────────────────────────────────────────────────────

export function EnterTasksView({
  onConnectIntegration,
  activePatientClaimIds,
}: {
  onConnectIntegration?: () => void;
  /**
   * Claim ids of the patient currently open in the EMR (from Real-Time Mode).
   * Tasks on these claims are PINNED to the top and highlighted — "here are the
   * tasks for the patient you're looking at". Passed as a prop rather than read
   * from the RTM store so this component stays identical across web/desktop
   * (desktop has no RTM host).
   */
  activePatientClaimIds?: readonly string[];
}) {
  const {
    tasks,
    allTaskCount,
    integration,
    needsAccount,
    isLoading,
    isError,
    refetch,
    closingIds,
    closeTask,
    postComment,
    scope,
    setScope,
  } = useEnterTasks();
  const filter = useTasksFilter();
  const setFilter = useSetTasksFilter();
  const typeFilter = useTasksTypeFilter();
  const setTypeFilter = useSetTasksTypeFilter();
  const onboarding = useTasksOnboarding();
  /** Task whose detail panel is open. Local, not store state — it is view
   *  navigation, and it must reset if the task leaves the list. */
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // `tasks` from the hook is already severity-filtered; the TYPE chip narrows it
  // further. Kept as a separate value so the chip COUNTS can be computed from
  // the severity-filtered set — a count that ignored the active severity would
  // promise more work than the chip actually shows.
  const visibleTasks = tasks.filter(t => taskMatchesType(t, typeFilter));

  // Counts drive which chips are OFFERED — a chip that filters to nothing is a
  // dead end the user only discovers by tapping it.
  const typeCounts = Object.fromEntries(
    TYPE_CHIPS.map(c => [c.id, tasks.filter(t => taskMatchesType(t, c.id)).length])
  ) as Record<TaskTypeId, number>;

  const severityChip = (value: typeof filter, label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => setFilter(value)}
      className={cn(
        'text-[11px] px-2 py-0.5 rounded-full border transition-colors',
        filter === value
          ? 'bg-primary/15 text-primary-text border-primary/30'
          : 'text-muted-foreground border-border hover:bg-muted/40'
      )}
    >
      {label}
    </button>
  );

  // Platform's header: title + a subtitle that states the OPEN workload.
  // "1 task" vs "2 tasks" is their copy, and the all-clear line replaces the
  // count rather than showing a zero.
  //
  // The subtitle must track the same states the body does. A count of 0 means
  // "caught up" ONLY when we actually fetched and got nothing — while the
  // request is held, failed, or there's no integration, "All caught up" is a
  // confident claim about a worklist we never read.
  const subtitle = needsAccount
    ? 'Select a practice to see its tasks.'
    : isError
      ? 'Tasks are unavailable right now.'
      : integration === 'none'
        ? 'Connect Enter to see your worklist.'
        : allTaskCount > 0
          ? `${allTaskCount} ${allTaskCount === 1 ? 'task needs' : 'tasks need'} your action`
          : 'All caught up — nothing open.';

  const header = (
    <div className="px-4 pt-3 pb-2 border-b border-border/40">
      <div className="flex items-center gap-2">
        <span className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/15 text-primary-text shrink-0">
          <ListChecks size={15} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-semibold text-foreground leading-tight">Tasks</p>
          <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onboarding.show}
          aria-label="How Tasks work"
          title="How Tasks work"
          className="p-1 rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
        >
          <Info size={14} />
        </button>
      </div>
    </div>
  );

  // Platform's TaskToolbar: a "My tasks" TOGGLE (not a My/All pair) sitting
  // outside the type bar, then the type filter chips. Counts appear only on
  // "All types" and "My tasks" — Platform puts a badge on those two and leaves
  // the rest bare, because a number on every chip turns a filter row into a
  // dashboard and stops reading as navigation.
  const toolbar =
    !isError && !needsAccount && integration !== 'none' ? (
      <div className="flex flex-col gap-1.5 px-4 py-2 border-b border-border/40">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setScope(scope === 'my' ? 'all' : 'my')}
            className={cn(
              'inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors',
              scope === 'my'
                ? 'bg-primary/15 text-primary-text border-primary/30'
                : 'text-muted-foreground border-border hover:bg-muted/40'
            )}
          >
            <User size={11} />
            My tasks
            {scope === 'my' && <span className="text-[10px] opacity-70">{tasks.length}</span>}
          </button>

          <span className="w-px h-4 bg-border/60" aria-hidden />

          {TYPE_CHIPS.filter(c => c.id === 'all' || typeCounts[c.id] > 0).map(c => {
            const Icon = c.icon;
            const active = typeFilter === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setTypeFilter(c.id)}
                className={cn(
                  'inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors',
                  active
                    ? 'bg-primary/15 text-primary-text border-primary/30'
                    : 'text-muted-foreground border-border hover:bg-muted/40'
                )}
              >
                <Icon size={11} />
                {c.label}
                {c.id === 'all' && <span className="text-[10px] opacity-70">{tasks.length}</span>}
              </button>
            );
          })}
        </div>

        {/* Severity kept as a second row. Platform dropped it, but our rows still
            carry the severity dot and a biller triaging by urgency would lose the
            only way to act on it. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {severityChip('all', 'All')}
          {SEVERITY_ORDER.map(sev => severityChip(sev, SEVERITY_LABEL[sev]))}
        </div>
      </div>
    ) : null;

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Centered>
        <Loader2 size={18} className="animate-spin" />
        <p className="text-[12px]">Loading tasks…</p>
      </Centered>
    );
  } else if (isError) {
    // A failed request (404 = gateway missing the route, 401, 500, offline) —
    // NOT the same as "no integration connected". Offer a retry.
    body = <ErrorState onRetry={() => void refetch()} />;
  } else if (needsAccount) {
    // Must come BEFORE the empty-state branches: with the request held, there
    // are legitimately zero tasks, and "You're all caught up" on an unpicked
    // multi-practice org is a confident lie about someone's worklist.
    body = (
      <Centered>
        <Building2 size={20} className="text-muted-foreground" />
        <p className="text-[12px]">Choose a practice to see its tasks.</p>
        <p className="text-[11px] text-muted-foreground">
          Your organization has more than one Enter account connected. Pick one from the account
          selector in the composer — tasks are always shown for a single practice.
        </p>
      </Centered>
    );
  } else if (integration === 'none') {
    body = <NoIntegration {...(onConnectIntegration ? { onConnectIntegration } : {})} />;
  } else if (allTaskCount === 0) {
    body = (
      <Centered>
        <Check size={20} className="text-emerald-500" />
        <p className="text-[12px]">You're all caught up — no open tasks.</p>
      </Centered>
    );
  } else if (visibleTasks.length === 0) {
    body = (
      <Centered>
        <p className="text-[12px]">
          No{' '}
          {typeFilter !== 'all'
            ? TASK_TYPE_DEFINITIONS.find(d => d.id === typeFilter)?.label.toLowerCase()
            : SEVERITY_LABEL[filter as EnterTask['severity']]}{' '}
          tasks.
        </p>
      </Centered>
    );
  } else {
    const row = (task: EnterTask, highlighted = false) => (
      <TaskRow
        key={task.id}
        task={task}
        onOpen={() => setSelectedId(task.id)}
        highlighted={highlighted}
      />
    );

    // Tasks for the patient currently open in the EMR float to the very top —
    // the whole point of the sidebar is "what's outstanding on THIS patient".
    const activeClaims = new Set(activePatientClaimIds ?? []);
    const isActive = (t: EnterTask) => !!t.claimId && activeClaims.has(t.claimId);
    const pinned = activeClaims.size > 0 ? visibleTasks.filter(isActive) : [];
    const rest = pinned.length > 0 ? visibleTasks.filter(t => !isActive(t)) : visibleTasks;

    // In the team view, split out work nobody owns — unclaimed tasks are the
    // ones most likely to be dropped, so they get their own labelled group
    // instead of being mixed in (or, as before, hidden entirely).
    const unassigned = scope === 'all' ? rest.filter(t => !t.assigneeId) : [];
    const assigned = scope === 'all' ? rest.filter(t => t.assigneeId) : rest;

    body = (
      <div className="flex flex-col">
        {pinned.length > 0 && (
          <>
            <div className="flex items-center gap-1.5 px-4 py-1.5 bg-primary/10 border-b border-primary/20">
              <Eye size={11} className="text-primary-text" />
              <span className="text-[11px] font-medium text-primary-text">Patient on screen</span>
              <span className="text-[11px] text-primary-text/80">{pinned.length}</span>
            </div>
            <div className="flex flex-col divide-y divide-border/60">
              {pinned.map(t => row(t, true))}
            </div>
          </>
        )}
        {assigned.length > 0 && (
          <div className="flex flex-col divide-y divide-border/60">{assigned.map(t => row(t))}</div>
        )}
        {unassigned.length > 0 && (
          <>
            <div className="flex items-center gap-1.5 px-4 py-1.5 bg-muted/30 border-y border-border/40">
              <UserX size={11} className="text-muted-foreground" />
              <span className="text-[11px] font-medium text-muted-foreground">Unassigned</span>
              <span className="text-[11px] text-muted-foreground">{unassigned.length}</span>
            </div>
            <div className="flex flex-col divide-y divide-border/60">{unassigned.map(t => row(t))}</div>
          </>
        )}
      </div>
    );
  }

  // Detail REPLACES the list rather than stacking over it. At sidebar width a
  // nested panel leaves ~40px of readable content, and Platform's own Tasks UX
  // is a full panel per task.
  const selectedIndex = selectedId ? visibleTasks.findIndex(t => t.id === selectedId) : -1;
  const selected = selectedIndex >= 0 ? visibleTasks[selectedIndex] : undefined;
  if (selected) {
    // Resolved from the CURRENT list, so it reflects the live filter/scope
    // rather than whatever the list looked like when the panel opened.
    const next = visibleTasks[selectedIndex + 1];
    return (
      <div className={PAGE_SHELL}>
        <div className={PAGE_COLUMN}>
          <TaskDetailPanel
          task={selected}
          onBack={() => setSelectedId(null)}
          onClose={() => setSelectedId(null)}
          onPostComment={commentBody => postComment(selected, commentBody)}
          closing={closingIds.has(selected.id)}
          hasNext={!!next}
          onCloseTask={async () => {
            const ok = await closeTask(selected);
            // Straight on to the next task — this is a worklist, and bouncing
            // back to the list between every close is the slow way through it.
            // Falls back to the list when this was the last one, so we never
            // strand the user on a task that no longer exists.
            if (ok) setSelectedId(next?.id ?? null);
            return ok;
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_COLUMN}>
        {header}
        {toolbar}
        <div className="flex-1 overflow-y-auto">{body}</div>
        <EnterTasksOnboarding
          open={onboarding.open}
          onDismiss={onboarding.dismiss}
          {...(onConnectIntegration ? { onConnectIntegration } : {})}
        />
      </div>
    </div>
  );
}
